import { createContext, useContext, useMemo, useState, useCallback, type ReactNode } from "react";

export interface TerritorySelection {
  id: number;
  name: string;
}

interface TerritoryContextValue {
  territory: TerritorySelection | null;
  selectTerritory(territory: TerritorySelection | null): void;
}

const STORAGE_KEY = "tolupanes:territory-context";
const TerritoryContext = createContext<TerritoryContextValue | null>(null);

function initialSelection(): TerritorySelection | null {
  try {
    const value = window.sessionStorage.getItem(STORAGE_KEY);
    if (!value) return null;
    const parsed = JSON.parse(value) as TerritorySelection;
    return Number.isInteger(parsed.id) && parsed.name ? parsed : null;
  } catch {
    return null;
  }
}

export function TerritoryProvider({ children }: { children: ReactNode }) {
  const [territory, setTerritory] = useState<TerritorySelection | null>(initialSelection);

  const selectTerritory = useCallback((next: TerritorySelection | null) => {
    setTerritory(next);
    if (next) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const value = useMemo<TerritoryContextValue>(() => ({
    territory,
    selectTerritory,
  }), [territory, selectTerritory]);

  return <TerritoryContext.Provider value={value}>{children}</TerritoryContext.Provider>;
}

export function useTerritory() {
  const value = useContext(TerritoryContext);
  if (!value) throw new Error("useTerritory must be used inside TerritoryProvider");
  return value;
}
